import React, { useState, useEffect, useRef } from 'react';

// Add TypewriterText component
const TypewriterText = ({ text, isActive, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!isActive) {
      if (!hasCompleted.current) {
        setDisplayedText('');
        setCurrentIndex(0);
      }
      return;
    }

    if (!hasCompleted.current && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, currentIndex === 0 ? delay : 90);

      return () => clearTimeout(timeout);
    }
  }, [isActive, currentIndex, text, delay]);

  useEffect(() => {
    if (currentIndex === text.length) {
      hasCompleted.current = true;
    }
  }, [currentIndex, text]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const FAQ = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [inView, setInView] = useState(false);
  const [visibleFAQs, setVisibleFAQs] = useState([]);
  const sectionRef = useRef(null);

  const faqs = [
    {
      question: "What is a freight forwarding company?",
      answer: "A freight forwarding company helps businesses move goods from one place to another across cities, countries or continents. We coordinate the entire process, from choosing the right transport (air, sea or road) to handling customs, warehousing and delivery."
    },
    {
      question: "Do I need a freight forwarder if I already have a courier?",
      answer: "Couriers handle small packages. Freight forwarders like Big Blue manage larger shipments, multiple containers, international cargo, and complex logistics with real tracking, documentation and end-to-end support. "
    },
    {
      question: "Can Big Blue help with international shipping?",
      answer: "Whether you’re importing into Canada & USA or exporting globally, we manage the full process by air freight, ocean freight, customs clearance and delivery."
    },
    {
      question: "What documents do I need to ship goods internationally?",
      answer: "We’ll guide you through all necessary documents, like commercial invoices, packing lists, bills of lading and customs paperwork so you don’t have to worry about getting it wrong."
    },
    {
      question: "Is warehousing available if my goods arrive early or need storage?",
      answer: "Yes. We offer bonded and non-bonded warehousing, with options for short- or long-term storage, repacking and inventory handling."
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);

          // Animate FAQs one by one
          let delay = 0;
          const delays = [];
          faqs.forEach((_, i) => {
            delays.push(setTimeout(() => {
              setVisibleFAQs(prev => [...prev, i]);
            }, delay));
            delay += 200; // 200ms between each FAQ
          });

          return () => delays.forEach(t => clearTimeout(t));
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 md:px-16">
      <style>
        {`
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .animate-slide-up {
            animation: slideUp 0.6s ease-out forwards;
          }

          /* New grid transition for smooth height changes */
          .faq-grid {
            display: grid;
            grid-template-rows: 0fr;
            transition: grid-template-rows 500ms ease-in-out;
          }
          
          .faq-grid.open {
            grid-template-rows: 1fr;
          }
          
          .faq-content {
            overflow: hidden;
            min-height: 0;
          }
        `}
      </style>

      <h2
        className={`text-center text-4xl md:text-5xl lg:text-[58px] font-bold text-[#002D6E] mb-10 min-h-[4rem] md:min-h-[5rem]`}
      >
        {inView && (
          <TypewriterText
            text="Frequently Asked Questions"
            isActive={inView}
            delay={0}
          />
        )}
      </h2>

      <div className="max-w-5xl mx-auto space-y-5 min-h-[50vh]">
        {faqs.map((faq, index) => {
          const isOpen = expandedIndex === index;
          const isVisible = visibleFAQs.includes(index);

          return (
            <div
              key={index}
              className={`transition-all duration-300 group ${isVisible ? 'opacity-100 animate-slide-up' : 'opacity-0'
                }`}
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <button
                onClick={() => setExpandedIndex(isOpen ? null : index)}
                className={`w-full flex items-stretch text-left border border-[#00439C] rounded-r-[14px] overflow-hidden transition-all duration-200 ${isOpen ? "bg-[#00439C]" : "bg-[#86CDE9] group-hover:bg-[#00439C]"
                  }`}
              >
                {/* Left Slanted Arrow Box */}
                <div
                  className={`w-[90px] flex items-center justify-center transition-all duration-300 ${isOpen
                      ? "bg-[#00B2FF]"
                      : "bg-[#86CDE9] group-hover:bg-[#00439C]"
                    }`}
                  style={{
                    clipPath: "polygon(0 0, 100% 0, 65% 100%, 0% 100%)",
                  }}
                >
                  <svg
                    className={`w-10 h-10 transition-transform duration-300 ${isOpen
                        ? "rotate-90 text-white"
                        : "text-[#00B2FF] group-hover:text-white"
                      }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 12h14m-4-6l4 6-4 6"
                    />
                  </svg>
                </div>

                {/* Question Text */}
                <div
                  className={`flex-1 px-4 py-3 text-[18px] font-medium transition-colors duration-200 ${isOpen
                      ? "text-white"
                      : "text-[#00439C] group-hover:text-white"
                    }`}
                >
                  {faq.question}
                </div>
              </button>

              {/* Modified Answer Section */}
              <div className={`faq-grid ${isOpen ? 'open' : ''}`}>
                <div className="faq-content">
                  <div className="pt-5 pb-7 px-[80px]">
                    <p className="text-lg text-[#4c4c4d] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FAQ;